/**
 * CategoryFormDialog — RHF+Zod create/edit dialog for a category (P1 Tier-A).
 * BottomSheet container (kit dialog). POST on create, PATCH on edit; only
 * changed fields go to the wire.
 *
 * PIM Wave P1 Task 6 extensions (design notes Surface 2, brief §2):
 *  - Name is now a single `TrilingualInput` (RU/KY/EN triple) instead of two
 *    separate RU/KY `Field`s — `name_en` (`CategoryCreate`/`CategoryUpdate`
 *    both carry it, `marketplace/schemas.py:608-639`) rides along for free.
 *  - Photo-rules fieldset (EDIT MODE ONLY — `photo_rules` is not on
 *    `CategoryCreate`, verified against `schemas.py:608-618`): an
 *    enable/disable switch + three optional numeric inputs, Zod cross-check
 *    `min_photos <= max_photos` (mirrors the backend's own constraint,
 *    `schemas.py:597-605`, so a bad pair 422s client-side instead of
 *    round-tripping).
 *  - Aliases chip editor (EDIT MODE ONLY — `AliasesEditor`, immediate
 *    mutations independent of this dialog's own save).
 *  - Edit submission moves from `useUpdateCategory`/`AdminCategoryUpdate`
 *    (a narrower shape lacking `name_en`/`photo_rules`) to `usePatchCategory`/
 *    `AdminCategoryPatch` (the full `CategoryUpdate` mirror from Task 5),
 *    assembling a changed-keys-only body via `buildPatchBody` — see its
 *    docstring for the `photo_rules` null-vs-omit rule.
 */
import { useCallback, type ReactElement } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { BottomSheet, Button, Field } from "@garden/ui";
import { TrilingualInput } from "@/features/admin/_shared";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useCreateCategory, usePatchCategory } from "@/features/admin/categories/hooks";
import { AliasesEditor } from "@/features/admin/categories/components/AliasesEditor";
import type {
  AdminCategory,
  AdminCategoryCreate,
  AdminCategoryPatch,
  PhotoRules,
} from "@/features/admin/categories/api";

interface PhotoRulesDraft {
  minPhotos: string;
  maxPhotos: string;
  minEdgePx: string;
}

const nameSchema = z.object({
  ru: z.string().min(1, "formError.required").max(200),
  ky: z.string().max(200),
  en: z.string().max(200),
});

const photoRulesDraftSchema = z.object({
  minPhotos: z.string(),
  maxPhotos: z.string(),
  minEdgePx: z.string(),
});

const schema = z
  .object({
    slug: z.string().min(1, "formError.required").max(128).regex(/^[a-z0-9-]+$/, "formError.slug"),
    name: nameSchema,
    parent_id: z.string(),
    sort_order: z.string().regex(/^\d+$/, "formError.numeric"),
    photoRulesEnabled: z.boolean(),
    photoRules: photoRulesDraftSchema,
  })
  .superRefine((data, ctx) => {
    if (!data.photoRulesEnabled) return;

    const parseNonNegative = (
      raw: string,
      path: "minPhotos" | "maxPhotos" | "minEdgePx",
      min: number,
    ): number | null => {
      const trimmed = raw.trim();
      if (trimmed === "") return null;
      if (!/^\d+$/.test(trimmed)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.numeric", path: ["photoRules", path] });
        return null;
      }
      const n = Number(trimmed);
      if (n < min) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.numeric", path: ["photoRules", path] });
        return null;
      }
      return n;
    };

    const minPhotos = parseNonNegative(data.photoRules.minPhotos, "minPhotos", 0);
    const maxPhotos = parseNonNegative(data.photoRules.maxPhotos, "maxPhotos", 1);
    parseNonNegative(data.photoRules.minEdgePx, "minEdgePx", 1);

    if (minPhotos !== null && maxPhotos !== null && minPhotos > maxPhotos) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "categories.form.photoRules.errors.minGtMax",
        path: ["photoRules", "maxPhotos"],
      });
    }
  });
type FormValues = z.infer<typeof schema>;

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

function photoRulesToDraft(rules: PhotoRules | null): PhotoRulesDraft {
  return {
    minPhotos: rules?.min_photos != null ? String(rules.min_photos) : "",
    maxPhotos: rules?.max_photos != null ? String(rules.max_photos) : "",
    minEdgePx: rules?.min_edge_px != null ? String(rules.min_edge_px) : "",
  };
}

/**
 * Draft -> wire, collapsing an "enabled but every field left blank" fieldset
 * to `null` (a hollow `{min_photos:null,max_photos:null,min_edge_px:null}`
 * object is behaviourally identical to no rules at all, so treat it as such
 * rather than writing a meaningless non-null `photo_rules`).
 */
function normalizedPhotoRules(enabled: boolean, draft: PhotoRulesDraft): PhotoRules | null {
  if (!enabled) return null;
  const min_photos = draft.minPhotos.trim() === "" ? null : Number(draft.minPhotos.trim());
  const max_photos = draft.maxPhotos.trim() === "" ? null : Number(draft.maxPhotos.trim());
  const min_edge_px = draft.minEdgePx.trim() === "" ? null : Number(draft.minEdgePx.trim());
  if (min_photos === null && max_photos === null && min_edge_px === null) return null;
  return { min_photos, max_photos, min_edge_px };
}

function photoRulesEqual(a: PhotoRules | null, b: PhotoRules | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.min_photos === b.min_photos && a.max_photos === b.max_photos && a.min_edge_px === b.min_edge_px;
}

/**
 * Changed-keys-only PATCH assembly (repo law: category writes are PATCH,
 * changed-keys-only, never PUT). `photo_rules` is one atomic field on the
 * wire (`CategoryUpdate.photo_rules: PhotoRules | null`, no server-side
 * per-key merge of the nested object), so its comparison is against the
 * FULL normalized shape rather than a per-subfield diff: the key is
 * included only when that whole shape changed vs. `original.photo_rules` —
 * omitted entirely when unchanged (PATCH leaves it alone), sent as an
 * explicit `null` when the fieldset held rules and was disabled (clears
 * it), sent as the built object when enabled with any filled field.
 */
function buildPatchBody(original: AdminCategory, values: FormValues): AdminCategoryPatch {
  const body: AdminCategoryPatch = {};

  const nextSlug = values.slug.trim();
  if (nextSlug !== original.slug) body.slug = nextSlug;

  const nextNameRu = values.name.ru.trim();
  if (nextNameRu !== original.name_ru) body.name_ru = nextNameRu;

  const nextNameKy = values.name.ky.trim() === "" ? null : values.name.ky.trim();
  if (nextNameKy !== original.name_ky) body.name_ky = nextNameKy;

  const nextNameEn = values.name.en.trim() === "" ? null : values.name.en.trim();
  if (nextNameEn !== original.name_en) body.name_en = nextNameEn;

  const nextParentId = values.parent_id === "" ? null : values.parent_id;
  if (nextParentId !== original.parent_id) body.parent_id = nextParentId;

  const nextSortOrder = Number(values.sort_order);
  if (nextSortOrder !== original.sort_order) body.sort_order = nextSortOrder;

  const nextPhotoRules = normalizedPhotoRules(values.photoRulesEnabled, values.photoRules);
  if (!photoRulesEqual(nextPhotoRules, original.photo_rules)) body.photo_rules = nextPhotoRules;

  return body;
}

export interface CategoryFormDialogProps {
  open: boolean;
  category: AdminCategory | null;
  parents: AdminCategory[];
  /** Preselects the parent select in CREATE mode only (`category === null`)
   * — the tree's "Add child" row action (P1 Task 5). Ignored in edit mode. */
  defaultParentId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function CategoryFormDialog({
  open,
  category,
  parents,
  defaultParentId = null,
  onClose,
  onSuccess,
}: CategoryFormDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const create = useCreateCategory();
  const patch = usePatchCategory();
  const isEdit = category !== null;

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      slug: category?.slug ?? "",
      name: { ru: category?.name_ru ?? "", ky: category?.name_ky ?? "", en: category?.name_en ?? "" },
      parent_id: category?.parent_id ?? defaultParentId ?? "",
      sort_order: String(category?.sort_order ?? 100),
      photoRulesEnabled: (category?.photo_rules ?? null) !== null,
      photoRules: photoRulesToDraft(category?.photo_rules ?? null),
    },
  });

  const photoRulesEnabled = watch("photoRulesEnabled");

  const errText = (key?: string): string | undefined => (key ? t(key) : undefined);

  const submit = useCallback(
    (values: FormValues) => {
      if (isEdit && category) {
        const body = buildPatchBody(category, values);
        patch.mutate(
          { id: category.id, body },
          { onSuccess: () => { toast.success(t("categories.form.updatedToast")); onSuccess(); }, onError: () => toast.error(t("error.generic")) },
        );
      } else {
        const body: AdminCategoryCreate = {
          slug: values.slug.trim(),
          name_ru: values.name.ru.trim(),
          name_ky: values.name.ky.trim() === "" ? null : values.name.ky.trim(),
          name_en: values.name.en.trim() === "" ? null : values.name.en.trim(),
          parent_id: values.parent_id === "" ? null : values.parent_id,
          sort_order: Number(values.sort_order),
        };
        create.mutate(body, {
          onSuccess: () => { toast.success(t("categories.form.createdToast")); onSuccess(); },
          onError: () => toast.error(t("error.generic")),
        });
      }
    },
    [isEdit, category, create, patch, onSuccess, t],
  );

  const isPending = create.isPending || patch.isPending;

  return (
    <BottomSheet open={open} onClose={onClose} title={t(isEdit ? "categories.form.editTitle" : "categories.form.createTitle")}>
      <form data-testid="category-form" onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
        <Field label={t("categories.form.slug")} data-testid="category-form-slug" error={errText(errors.slug?.message)} {...register("slug")} />

        <Controller
          control={control}
          name="name"
          render={({ field: nameField }) => (
            <TrilingualInput
              id="category-form-name"
              label={t("categories.form.name")}
              value={nameField.value}
              onChange={nameField.onChange}
              required
              error={errText(errors.name?.ru?.message)}
            />
          )}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="category-form-parent" className="text-[13px] font-semibold text-foreground">{t("categories.form.parent")}</label>
          <select id="category-form-parent" data-testid="category-form-parent_id" className={SELECT_CLASS} {...register("parent_id")}>
            <option value="">{t("categories.form.noParent")}</option>
            {parents.filter((p) => p.id !== category?.id).map((p) => (
              <option key={p.id} value={p.id}>{p.name_ru}</option>
            ))}
          </select>
        </div>
        <Field label={t("categories.form.sortOrder")} data-testid="category-form-sort_order" inputMode="numeric" error={errText(errors.sort_order?.message)} {...register("sort_order")} />

        {isEdit ? (
          <fieldset
            data-testid="category-form-photo-rules"
            className="flex flex-col gap-3 rounded-[var(--g-radius)] border border-[var(--g-hairline)] p-3"
          >
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
              {t("categories.form.photoRules.legend")}
            </legend>
            <label className="flex min-h-[44px] items-center gap-2 text-sm font-medium text-foreground">
              <input
                type="checkbox"
                data-testid="category-form-photo-rules-enabled"
                className="size-5 accent-[var(--g-primary)]"
                {...register("photoRulesEnabled")}
              />
              {t("categories.form.photoRules.enableLabel")}
            </label>
            {photoRulesEnabled ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <div className="flex-1">
                  <Field
                    label={t("categories.form.photoRules.minPhotos")}
                    data-testid="category-form-photo-rules-min-photos"
                    inputMode="numeric"
                    hint={t("categories.form.photoRules.minPhotosHint")}
                    error={errText(errors.photoRules?.minPhotos?.message)}
                    {...register("photoRules.minPhotos")}
                  />
                </div>
                <div className="flex-1">
                  <Field
                    label={t("categories.form.photoRules.maxPhotos")}
                    data-testid="category-form-photo-rules-max-photos"
                    inputMode="numeric"
                    hint={t("categories.form.photoRules.maxPhotosHint")}
                    error={errText(errors.photoRules?.maxPhotos?.message)}
                    {...register("photoRules.maxPhotos")}
                  />
                </div>
                <div className="flex-1">
                  <Field
                    label={t("categories.form.photoRules.minEdgePx")}
                    data-testid="category-form-photo-rules-min-edge-px"
                    inputMode="numeric"
                    hint={t("categories.form.photoRules.minEdgePxHint")}
                    error={errText(errors.photoRules?.minEdgePx?.message)}
                    {...register("photoRules.minEdgePx")}
                  />
                </div>
              </div>
            ) : null}
          </fieldset>
        ) : null}

        {isEdit && category ? <AliasesEditor categoryId={category.id} /> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isPending} className="min-h-[44px]">{t("common.cancel")}</Button>
          <Button type="submit" variant="primary" size="sm" data-testid="category-form-submit" loading={isPending} className="min-h-[44px]">{t("categories.form.submit")}</Button>
        </div>
      </form>
    </BottomSheet>
  );
}

export default CategoryFormDialog;
